import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { When, If, Then, Else, Switch, Case } from 'react-if';
import { Row, Col, Container } from 'react-bootstrap';
import firebase from 'firebase/app';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import * as QUESTION from '../../../../constants/questions';
import { alphabet } from '../../../../static/utils';
import './styles.css';
import { ReactComponent as PayIcon } from './pay_icon.svg';
import { ReactComponent as AlreadyPaidIcon } from './Already_paid_icon.svg';
import { useToggle } from 'react-use';
import MakePaymentModal from '../MakePaymentModal';
import Button from '../../../../components/button';
import { ReactComponent as ReplyIcon } from './reply-icon.svg';
import { useReplies } from '../../useReplies';
import Reply from './Reply';
import { IS_SIDEKICK_APP } from '../../../../constants/projects';

const YES_BUTTON_TEXT = 'YES';

const Review = ({
    id,
    alreadyPaid,
    postType,
    review,
    isPostOwner,
    postId,
    onPaySuccess,
    onMeasure,
    onRemeasure,
}) => {
    const [user] = useDocumentData(firebase.firestore().doc(`users/${review.publisherUid}`));
    const [isMakePaymentModalOpens, toggleMakePaymentModal] = useToggle(false);
    const [isRepliesOpens, toggleRepliesOpenStatus] = useToggle(false);
    const [replyText, setReplyText] = useState('');
    const textareaRef = useRef(null);
    const { replies, count, addReply, loadMore } = useReplies(id);

    const isReviewButtonDisabled = replyText.length <= 0;
    const haveRepliesButton = replies.length < count;

    useEffect(() => {
        if (isRepliesOpens) {
            if (window.ResizeObserver) {
                const observer = new window.ResizeObserver(() => {
                    onMeasure();
                });

                observer.observe(textareaRef.current);
                return () => observer.disconnect();
            }
        }
    }, [isRepliesOpens]);

    const handleReplyChange = useCallback(e => {
        setReplyText(e.target.value);
    }, []);

    const handleRepliesButtonClick = useCallback(() => {
        // TODO added on 7/3/2020 by pavel.karpovich: add if when no replies
        toggleRepliesOpenStatus();
        onRemeasure();
    }, [toggleRepliesOpenStatus, onRemeasure]);

    const handlePublishButtonClick = useCallback(async () => {
        await addReply(id, replyText);
        setReplyText('');
        onRemeasure();
    }, [id, replyText, addReply]);

    const handleLoadMore = useCallback(() => {
        loadMore();
        onRemeasure();
    }, [loadMore]);

    return (
        <div className="post-review">
            <Container>
                <Row>
                    <Col lg={2} className="d-flex justify-content-center align-items-center">
                        <Switch>
                            <Case condition={postType === QUESTION.YES_NO}>
                                <If
                                    condition={
                                        review.questions[0] &&
                                        review.questions[0].title === YES_BUTTON_TEXT
                                    }
                                >
                                    <Then>
                                        <div className="post-review__question-yes">
                                            <p>
                                                {review.questions[0] && review.questions[0].title}
                                            </p>
                                        </div>
                                    </Then>
                                    {/* <Else>
                                        <div className="post-review__question-no">
                                            <p>
                                                {review.questions[0] && review.questions[0].title}
                                            </p>
                                        </div>
                                    </Else> */}
                                </If>
                            </Case>
                            <Case condition={postType === QUESTION.MULTIPLE}>
                                <div className="post-review__question-multiple d-flex">
                                    {review.questions.slice(0, 3).map((question, index) => (
                                        <div
                                            key={index}
                                            className="post-review__question-multiple-item ml-2"
                                        >
                                            {alphabet[question.order]}
                                            <When condition={index === 2}>+</When>
                                        </div>
                                    ))}
                                </div>
                            </Case>
                        </Switch>
                    </Col>
                    <Col lg={7}>
                        <div className="post-review__body-text">
                            <p>{review.text}</p>
                        </div>
                        <div>
                            <When condition={!!user}>
                                {() => (
                                    <Then>
                                        <div className="align-items-baseline d-flex">
                                            <p className="post-review__user mr-3">
                                                {user.lastName
                                                    ? `${user.firstName} ${user.lastName}. `
                                                    : `${user.firstName}. `}
                                                {user.position}
                                            </p>
                                            <When condition={!!user.earned}>
                                                <p className="post-review__user-earned mr-3">
                                                    Earned: £{user.earned || 0}
                                                </p>
                                            </When>
                                            <When condition={isPostOwner}>
                                                <a
                                                    className="post-review__user"
                                                    href={`mailto:${user.email}`}
                                                >
                                                    {user.email}
                                                </a>
                                            </When>
                                        </div>
                                    </Then>
                                )}
                            </When>
                        </div>
                    </Col>
                    <Col
                        lg={3}
                        className="d-flex flex-column justify-content-center align-items-baseline"
                    >
                        <When condition={isPostOwner}>
                            <If condition={alreadyPaid}>
                                <Then>
                                    <p className="post-review__thanks-button--paid align-items-center d-flex justify-content-around">
                                        Already paid
                                        <AlreadyPaidIcon />
                                    </p>
                                </Then>
                                <Else>
                                    <p
                                        className="align-items-center d-flex justify-content-around post-review__thanks-button"
                                        onClick={toggleMakePaymentModal}
                                    >
                                        <If condition={IS_SIDEKICK_APP}>
                                            <Then>Pay</Then>
                                            <Else>Say thanks</Else>
                                        </If>
                                        <PayIcon />
                                    </p>
                                </Else>
                            </If>
                        </When>
                        {/* <div
                            className="align-items-center d-flex justify-content-center cursor-pointer mt-3"
                            onClick={handleRepliesButtonClick}
                        >
                            <ReplyIcon />
                            <span className="ml-2 reply__show-button"> {count} Reply</span>
                        </div> */}
                    </Col>
                </Row>
                <When condition={isRepliesOpens}>
                    {() => (
                        <div className="mt-3">
                            {replies.map(r => (
                                <Reply key={r.id} text={r.text} createdBy={r.createdBy} />
                            ))}
                            <Row>
                                <When condition={haveRepliesButton}>
                                    {() => (
                                        <>
                                            <Col lg={2} />
                                            <Col lg={7}>
                                                <div className="reviews__pagination d-flex justify-content-center align-items-center mb-3">
                                                    <span
                                                        className="reviews__pagination-load-more"
                                                        onClick={handleLoadMore}
                                                    >
                                                        Load more
                                                    </span>
                                                </div>
                                            </Col>
                                            <Col lg={3} />
                                        </>
                                    )}
                                </When>
                            </Row>
                            <Row>
                                <Col lg={2} />
                                <Col lg={7} className="reply__add-area-wrapper">
                                    <textarea
                                        ref={textareaRef}
                                        className="w-100 px-3 reply__add-area"
                                        rows={1}
                                        value={replyText}
                                        onChange={handleReplyChange}
                                    />
                                </Col>
                                <Col lg={3}>
                                    <div>
                                        <Button
                                            text="Publish"
                                            isDisabled={isReviewButtonDisabled}
                                            classes="reply__publish-button"
                                            onClick={handlePublishButtonClick}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </When>
            </Container>
            <When condition={isMakePaymentModalOpens}>
                {() => (
                    <MakePaymentModal
                        postId={postId}
                        isOpen={isMakePaymentModalOpens}
                        onToggle={toggleMakePaymentModal}
                        reviewId={id}
                        recipientId={user.uid}
                        recipientEmail={user.email}
                        recipientFirstName={user.firstName}
                        recipientStripeUserId={user.stripeUserId}
                        onPaySuccess={onPaySuccess}
                    />
                )}
            </When>
        </div>
    );
};

Review.propTypes = {
    postId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    postType: PropTypes.number.isRequired,
    review: PropTypes.object.isRequired,
    isPostOwner: PropTypes.bool.isRequired,
    alreadyPaid: PropTypes.bool,
    onMeasure: PropTypes.func.isRequired,
    onRemeasure: PropTypes.func.isRequired,
    onPaySuccess: PropTypes.func.isRequired,
};

Review.defaultProps = {
    alreadyPaid: false,
};

export default React.memo(Review);
